import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, GlueJob, GlueJobRun, ServiceCardData } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import { MonitoringService, MonitoringFinding } from '../../core/services/monitoring.service';
import {
  CloudFilters,
  FacetDef,
  SortOption,
  parseFiltersFromParams,
  filtersToParams,
} from './cloud-filter.model';
import { ActivityItem, findingsToActivity, glueRunsToActivity, sortActivity } from './cloud-activity.model';

type Tab = 'overview' | 'jobs' | 'activity';

const DEFAULT_SORT = 'last-run-desc';
const FACET_KEYS   = ['status', 'monitoring'];

const SORT_OPTIONS: SortOption[] = [
  { value: 'last-run-desc',   label: 'Most recent'    },
  { value: 'name-asc',        label: 'Name A → Z'     },
  { value: 'name-desc',       label: 'Name Z → A'     },
  { value: 'duration-desc',   label: 'Longest run'    },
  { value: 'monitored-first', label: 'Monitored first' },
];

const FACETS: FacetDef[] = [
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'SUCCEEDED', label: 'Succeeded' },
      { value: 'FAILED',    label: 'Failed'    },
      { value: 'RUNNING',   label: 'Running'   },
      { value: 'STOPPED',   label: 'Stopped'   },
    ],
  },
  {
    key: 'monitoring',
    label: 'Monitoring',
    options: [
      { value: 'monitored',   label: 'Monitored'     },
      { value: 'unmonitored', label: 'Not monitored' },
    ],
  },
];

@Component({
  selector: 'app-glue-detail',
  templateUrl: './glue-detail.component.html',
})
export class GlueDetailComponent implements OnInit, OnDestroy {
  selectedTab: Tab = 'overview';
  jobs: GlueJob[] = [];
  loading = true;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };

  readonly tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'jobs',     label: 'Jobs'     },
    { id: 'activity', label: 'Activity' },
  ];

  readonly facets    = FACETS;
  readonly sortOptions = SORT_OPTIONS;

  readonly serviceCard: ServiceCardData = { id: 'glue', name: 'Glue', icon: 'database', route: '/cloud/glue', status: 'healthy', metrics: [] };

  selectedJob: string | null = null;
  drawerItemId: string | null = null;
  jobRuns: GlueJobRun[] = [];
  runsLoading = false;
  runsError: string | null = null;
  runsPage = 1;
  readonly runsPageSize = 10;

  activity: ActivityItem[] = [];
  activityLoading = true;
  refreshing = false;
  private allFindings: MonitoringFinding[] = [];

  private sub?: Subscription;
  private routeSub?: Subscription;
  private runsSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    this.cloud.loadItemPoliciesForService('glue').subscribe();
    this.routeSub = this.route.queryParams.subscribe(params => {
      this.filters = parseFiltersFromParams(params as Record<string, string>, DEFAULT_SORT, FACET_KEYS);
      const jobName = (params['job'] as string | undefined) ?? null;
      if (jobName !== this.selectedJob) {
        this.selectedJob = jobName;
        this.runsPage = 1;
        if (jobName) {
          this.loadJobRuns();
        } else {
          this.jobRuns = [];
          this.runsSub?.unsubscribe();
        }
      }
    });
    this.loadData(false);
  }

  /** Loads the job list (cache-aware), monitoring state (monitored count), and activity. */
  private loadData(bypass: boolean): void {
    this.sub?.unsubscribe();
    this.sub = this.cloud.getGlueJobs(bypass).subscribe(jobs => {
      this.jobs       = jobs;
      this.loading    = false;
      this.refreshing = false;
      this.rebuildActivity();
    });
    this.cloud.loadMonitoringState().subscribe();
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(findings => {
      this.allFindings     = findings;
      this.activityLoading = false;
      this.rebuildActivity();
    });
  }

  /** Manual refresh — bypass the cache and reload (guarded against double-clicks). */
  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadData(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.runsSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  // ── Summary counts (standardized: total + monitored) ────────────────────────

  get totalCount():     number { return this.jobs.length; }
  get monitoredCount(): number { return this.jobs.filter(j => this.cloud.isMonitored('glue', j.name)).length; }

  // ── Activity feed ───────────────────────────────────────────────────────────

  private rebuildActivity(): void {
    this.activity = sortActivity([
      ...findingsToActivity(this.allFindings, 'GLUE'),
      ...glueRunsToActivity(this.jobs),
    ]);
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'jobs';
    this.selectJob(item.resourceId);
  }

  // ── Overview counts (unfiltered) ────────────────────────────────────────────

  get failedCount():    number { return this.jobs.filter(j => j.lastRunStatus === 'FAILED').length; }
  get runningCount():   number { return this.jobs.filter(j => j.lastRunStatus === 'RUNNING').length; }
  get succeededCount(): number { return this.jobs.filter(j => j.lastRunStatus === 'SUCCEEDED').length; }

  // ── Filtered + sorted list ──────────────────────────────────────────────────

  get filteredJobs(): GlueJob[] {
    let result = this.jobs;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(j => j.name.toLowerCase().includes(q));

    const statuses = this.filters.facets['status'] ?? [];
    if (statuses.length) result = result.filter(j => j.lastRunStatus != null && statuses.includes(j.lastRunStatus));

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(j => {
        const mon = this.cloud.isMonitored('glue', j.name);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }

    return this.sorted(result, this.filters.sort);
  }

  private sorted(jobs: GlueJob[], sort: string): GlueJob[] {
    return [...jobs].sort((a, b) => {
      switch (sort) {
        case 'name-asc':      return a.name.localeCompare(b.name);
        case 'name-desc':     return b.name.localeCompare(a.name);
        case 'duration-desc': return (b.duration ?? '').localeCompare(a.duration ?? '');
        case 'monitored-first': {
          const aM = this.cloud.isMonitored('glue', a.name) ? 0 : 1;
          const bM = this.cloud.isMonitored('glue', b.name) ? 0 : 1;
          return aM - bM;
        }
        default:              return (b.lastRunAt ?? '').localeCompare(a.lastRunAt ?? '');
      }
    });
  }

  selectJob(name: string): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         { job: name },
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  closeJob(): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         { job: null },
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  private loadJobRuns(): void {
    if (!this.selectedJob) return;
    this.runsLoading = true;
    this.runsError   = null;
    this.runsSub?.unsubscribe();
    this.runsSub = this.cloud.getGlueJobRuns(this.selectedJob).subscribe({
      next: (runs) => {
        this.jobRuns     = runs;
        this.runsLoading = false;
        this.runsPage    = Math.min(this.runsPage, Math.max(1, this.runsTotalPages));
      },
      error: () => {
        this.runsError   = 'Failed to load run history.';
        this.runsLoading = false;
      },
    });
  }

  get runsTotalPages(): number {
    return Math.max(1, Math.ceil(this.jobRuns.length / this.runsPageSize));
  }

  get pagedJobRuns(): GlueJobRun[] {
    const start = (this.runsPage - 1) * this.runsPageSize;
    return this.jobRuns.slice(start, start + this.runsPageSize);
  }

  runStatusClass(status: string): string {
    const map: Record<string, string> = {
      SUCCEEDED: 'bg-emerald-100 text-emerald-700',
      FAILED:    'bg-red-100 text-red-700',
      TIMEOUT:   'bg-red-100 text-red-700',
      ERROR:     'bg-red-100 text-red-700',
      RUNNING:   'bg-blue-100 text-blue-700',
      STARTING:  'bg-blue-100 text-blue-600',
      STOPPING:  'bg-slate-100 text-slate-500',
      STOPPED:   'bg-slate-100 text-slate-500',
      WAITING:   'bg-amber-100 text-amber-700',
    };
    return map[status] ?? 'bg-slate-100 text-slate-400';
  }

  onFiltersChange(f: CloudFilters): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         filtersToParams(f, FACET_KEYS),
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Monitoring quota ────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(name: string): boolean { return this.cloud.isMonitored('glue', name); }

  toggleMonitoring(name: string): void {
    const current = this.cloud.isMonitored('glue', name);
    if (!current && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    this.cloud.toggleMonitoring('glue', name, !current).subscribe({
      error: err => {
        if (err.status === 403) {
          const msg = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
          this.toast.show(msg, 'error');
        }
      },
    });
  }

  // ── Item settings drawer ────────────────────────────────────────────────────

  openItemSettings(name: string): void  { this.drawerItemId = name; }
  closeItemSettings(): void             { this.drawerItemId = null; }

  itemOverrideSummary(name: string): string {
    return this.cloud.getItemOverrideSummary('glue', name);
  }

  // ── Status badge ────────────────────────────────────────────────────────────

  statusClass(status: GlueJob['lastRunStatus']): string {
    const map: Record<string, string> = {
      SUCCEEDED: 'bg-emerald-100 text-emerald-700',
      FAILED:    'bg-red-100 text-red-700',
      RUNNING:   'bg-blue-100 text-blue-700',
      STOPPED:   'bg-slate-100 text-slate-500',
    };
    return (status ? map[status] : null) ?? 'bg-slate-100 text-slate-400';
  }
}
